/**
 * Billing and Invoice Enums
 *
 * Enums for billing, invoicing, and payment statuses.
 * Issue #313 - Centralize enums to shared-types
 *
 * Note: SubscriptionStatus is defined in enums/roles.ts
 * Note: Zod schemas moved to dtos/billing.dto.ts to avoid circular deps
 */

// ============================================================================
// INVOICE STATUS
// ============================================================================

/**
 * Invoice lifecycle status
 */
export enum InvoiceStatus {
  /** Invoice is being drafted, not yet sent */
  DRAFT = 'draft',
  /** Invoice has been sent to the customer */
  SENT = 'sent',
  /** Invoice has been paid */
  PAID = 'paid',
  /** Invoice is past due date */
  OVERDUE = 'overdue',
  /** Invoice has been cancelled */
  CANCELLED = 'cancelled',
  /** Invoice has been voided (accounting reversal) */
  VOID = 'void',
}

/** Array of all invoice statuses for iteration */
export const INVOICE_STATUSES = Object.values(InvoiceStatus);

// ============================================================================
// PAYMENT STATUS
// ============================================================================

/**
 * Payment processing status
 */
export enum PaymentStatus {
  /** Payment is pending processing */
  PENDING = 'pending',
  /** Payment completed successfully */
  SUCCEEDED = 'succeeded',
  /** Payment failed */
  FAILED = 'failed',
  /** Payment was refunded */
  REFUNDED = 'refunded',
}

/** Array of all payment statuses for iteration */
export const PAYMENT_STATUSES = Object.values(PaymentStatus);

// ============================================================================
// BILLING PERIOD
// ============================================================================

/**
 * Billing period/interval for subscriptions
 */
export enum BillingPeriod {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUALLY = 'annually',
}

/** Array of all billing periods for iteration */
export const BILLING_PERIODS = Object.values(BillingPeriod);

// ============================================================================
// DISCOUNT TYPE
// ============================================================================

/**
 * Types of discounts that can be applied
 */
export enum DiscountType {
  PERCENTAGE = 'percentage',
  FIXED_AMOUNT = 'fixed_amount',
}

// ============================================================================
// REFUND STATUS
// ============================================================================

/**
 * Refund processing status
 */
export enum RefundStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/** Array of all refund statuses for iteration */
export const REFUND_STATUSES = Object.values(RefundStatus);

// ============================================================================
// PAYMENT METHOD TYPE
// ============================================================================

/**
 * Types of payment methods
 */
export enum PaymentMethodType {
  CARD = 'card',
  BANK_ACCOUNT = 'bank_account',
  ACH = 'ach',
  WIRE = 'wire',
}

/** Array of all payment method types for iteration */
export const PAYMENT_METHOD_TYPES = Object.values(PaymentMethodType);
